import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { parseOFX, isValidOFX, OFXTransaction } from '@/utils/ofxParser';
import { parseExcel, isValidExcel, ExcelTransaction } from '@/utils/excelParser';
import { criarContaReceber } from '@/integrations/supabase/contas-receber';
import { criarContaPagar } from '@/integrations/supabase/contas-pagar';

interface DialogImportarTransacoesProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type Transacao = OFXTransaction | ExcelTransaction;

export function DialogImportarTransacoes({ isOpen, onClose, onSuccess }: DialogImportarTransacoesProps) {
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [transacoes, setTransacoes] = useState<Transacao[]>([]);
  const [etapa, setEtapa] = useState<'upload' | 'preview' | 'importando'>('upload');
  const [categoriaPadrao, setCategoriaPadrao] = useState<string>('Importação');
  const [isImporting, setIsImporting] = useState(false);

  const handleArquivoSelecionado = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;

    try {
      setArquivo(arquivo);
      const conteudo = await lerArquivo(arquivo);

      let transacoes: Transacao[] = [];

      if (isValidOFX(conteudo)) {
        transacoes = parseOFX(conteudo);
      } else if (isValidExcel(arquivo)) {
        transacoes = await parseExcel(arquivo);
      } else {
        toast.error('Formato de arquivo não suportado. Use OFX ou Excel (.xlsx)');
        return;
      }

      if (transacoes.length === 0) {
        toast.error('Nenhuma transação encontrada no arquivo');
        return;
      }

      setTransacoes(transacoes);
      setEtapa('preview');
      toast.success(`${transacoes.length} transações encontradas`);
    } catch (erro: any) {
      toast.error(erro.message || 'Erro ao ler arquivo');
      setArquivo(null);
    }
  };

  const lerArquivo = (arquivo: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };
      reader.onerror = () => {
        reject(new Error('Erro ao ler arquivo'));
      };
      reader.readAsText(arquivo);
    });
  };

  const handleImportar = async () => {
    if (transacoes.length === 0) return;

    setIsImporting(true);
    setEtapa('importando');

    try {
      let sucessos = 0;
      let erros = 0;

      for (const transacao of transacoes) {
        try {
          if (transacao.tipo === 'recebimento') {
            const resultado = await criarContaReceber({
              descricao: transacao.descricao,
              categoria: 'categoria' in transacao ? transacao.categoria : categoriaPadrao,
              valor_total: transacao.valor,
              data_vencimento: transacao.data,
              status: 'recebido',
              data_recebimento: transacao.data,
              valor_recebido: transacao.valor,
            });

            if (resultado.ok) {
              sucessos++;
            } else {
              erros++;
            }
          } else {
            const resultado = await criarContaPagar({
              descricao: transacao.descricao,
              categoria: 'categoria' in transacao ? transacao.categoria : categoriaPadrao,
              valor_total: transacao.valor,
              data_vencimento: transacao.data,
              status: 'pago',
              data_pagamento: transacao.data,
              valor_pago: transacao.valor,
            });

            if (resultado.ok) {
              sucessos++;
            } else {
              erros++;
            }
          }
        } catch (erro) {
          console.warn('Erro ao importar transação:', erro);
          erros++;
        }
      }

      toast.success(`Importação concluída: ${sucessos} OK, ${erros} erros`);

      if (onSuccess) {
        onSuccess();
      }

      // Resetar depois de sucesso
      setTimeout(() => {
        setEtapa('upload');
        setArquivo(null);
        setTransacoes([]);
        onClose();
      }, 1500);
    } catch (erro: any) {
      toast.error(erro.message || 'Erro ao importar transações');
      setEtapa('preview');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importar Transações</DialogTitle>
          <DialogDescription>
            Importe transações de arquivos OFX ou Excel para popular o Fluxo de Caixa
          </DialogDescription>
        </DialogHeader>

        {etapa === 'upload' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <Label htmlFor="arquivo-input" className="cursor-pointer">
                <div className="text-sm font-medium text-gray-700 mb-1">
                  Selecione um arquivo OFX ou Excel
                </div>
                <div className="text-xs text-gray-500">
                  Arraste e solte ou clique para procurar
                </div>
              </Label>
              <Input
                id="arquivo-input"
                type="file"
                accept=".ofx,.xlsx,.xls"
                onChange={handleArquivoSelecionado}
                className="hidden"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
              <p className="font-medium mb-2">Formatos suportados:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li><strong>OFX:</strong> Extratos bancários padrão (ITAU, Bradesco, etc)</li>
                <li><strong>Excel:</strong> Colunas: Data | Descrição | Valor | Tipo</li>
              </ul>
            </div>
          </div>
        )}

        {etapa === 'preview' && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2 text-sm">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-green-900">{transacoes.length} transações encontradas</p>
                <p className="text-xs text-green-800">Revise antes de importar</p>
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Categoria padrão (se não especificada)</Label>
              <Select value={categoriaPadrao} onValueChange={setCategoriaPadrao}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Importação">Importação</SelectItem>
                  <SelectItem value="Outros">Outros</SelectItem>
                  <SelectItem value="Serviços">Serviços</SelectItem>
                  <SelectItem value="Produtos">Produtos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="text-xs">Data</TableHead>
                    <TableHead className="text-xs">Descrição</TableHead>
                    <TableHead className="text-xs text-right">Valor</TableHead>
                    <TableHead className="text-xs">Tipo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transacoes.slice(0, 10).map((t, i) => (
                    <TableRow key={i} className="text-xs">
                      <TableCell className="font-mono">{t.data}</TableCell>
                      <TableCell className="max-w-xs truncate">{t.descricao}</TableCell>
                      <TableCell className="text-right font-mono">
                        {t.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          t.tipo === 'recebimento'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {t.tipo === 'recebimento' ? 'Receita' : 'Despesa'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {transacoes.length > 10 && (
                <div className="bg-gray-50 px-4 py-2 text-xs text-gray-600 border-t">
                  ... e mais {transacoes.length - 10} transações
                </div>
              )}
            </div>
          </div>
        )}

        {etapa === 'importando' && (
          <div className="flex flex-col items-center justify-center py-12 space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="text-sm text-gray-600">Importando {transacoes.length} transações...</p>
          </div>
        )}

        <DialogFooter>
          {etapa === 'upload' && (
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          )}

          {etapa === 'preview' && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setEtapa('upload');
                  setTransacoes([]);
                  setArquivo(null);
                }}
              >
                Voltar
              </Button>
              <Button onClick={handleImportar} disabled={isImporting}>
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importando...
                  </>
                ) : (
                  'Importar Transações'
                )}
              </Button>
            </>
          )}

          {etapa === 'importando' && (
            <Button disabled>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Aguarde...
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
